import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatError, money, categoryLabel, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Countdown from "@/components/Countdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MapPin, Gavel, Eye, Building2, Ruler, Package, Heart, CheckCircle2, CreditCard, Zap, Sparkles, Wifi, ScrollText } from "lucide-react";
import RulesModal from "@/components/RulesModal";

export default function AuctionDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [bidAmount, setBidAmount] = useState("");
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState("");
  const [activeImg, setActiveImg] = useState(0);
  const [watching, setWatching] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [boostLoading, setBoostLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const wsRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        api.get(`/auctions/${id}`),
        api.get(`/auctions/${id}/bids?limit=25`),
      ]);
      setAuction(a.data);
      setBids(b.data);
    } catch (e) {
      toast.error(formatError(e));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Load rules acceptance status for the current user
  useEffect(() => {
    if (!id || !user || typeof user !== "object") return;
    api.get(`/auctions/${id}/rules`)
      .then((r) => setRulesAccepted(!!r.data.accepted))
      .catch((err) => console.warn("Failed to load rules status:", err));
  }, [id, user]);

  // Real-time bid updates via WebSocket (replaces 5s polling)
  useEffect(() => {
    if (!id) return;
    const wsProto = BACKEND_URL.startsWith("https") ? "wss" : "ws";
    const wsUrl = BACKEND_URL.replace(/^https?/, wsProto) + `/api/ws/auctions/${id}`;
    let ws;
    let reconnectTimer;
    let closed = false;

    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => setWsConnected(true);
        ws.onclose = () => {
          setWsConnected(false);
          if (!closed) reconnectTimer = setTimeout(connect, 3000);
        };
        ws.onerror = (e) => {
          console.warn("Auction WebSocket error:", e);
          try { ws.close(); } catch (closeErr) { console.warn("WS close failed:", closeErr); }
        };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "bid" && msg.auction) {
              setAuction(msg.auction);
              setBids((prev) => {
                const newBid = {
                  id: `ws-${msg.bid.created_at}`,
                  amount: msg.bid.amount,
                  bidder_name: msg.bid.bidder_name,
                  user_id: msg.bid.user_id,
                  created_at: msg.bid.created_at,
                };
                const dedup = prev.filter((b) => !(b.amount === newBid.amount && b.bidder_name === newBid.bidder_name && b.created_at === newBid.created_at));
                return [newBid, ...dedup].sort((a, b) => b.amount - a.amount).slice(0, 25);
              });
              if (msg.extended) toast.info("Timer extended — someone bid at the last second!");
            }
          } catch (err) {
            console.warn("Failed to parse WebSocket message:", err);
          }
        };
      } catch (err) {
        console.warn("Failed to open auction WebSocket:", err);
      }
    };
    connect();
    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws && ws.close(); } catch (err) { console.warn("WS teardown close failed:", err); }
    };
  }, [id]);

  if (!auction) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-zinc-500 font-display uppercase tracking-widest">
        Loading auction…
      </div>
    );
  }

  const isAuthed = user && typeof user === "object";
  const isOwner = isAuthed && auction.seller_id === user.id;
  const isWinner = isAuthed && auction.winner_id === user.id;
  const canBid = auction.status === "active" && !isOwner;
  const isPaid = auction.payment_status === "paid";
  const currentBid = auction.current_bid || auction.starting_bid;
  const increment = auction.bid_increment || 5;
  const minBid = Math.max(auction.starting_bid, currentBid + (auction.current_bid > 0 ? increment : 0));
  const galleryImages = (auction.photos && auction.photos.length > 0)
    ? auction.photos.map((p) => `${process.env.REACT_APP_BACKEND_URL}/api/uploads/file/${p}`)
    : (auction.hero_image ? [auction.hero_image] : ["https://images.unsplash.com/photo-1649313444539-a8900c5cdc54?w=1200&auto=format&fit=crop&q=80"]);

  const placeBid = async (e) => {
    e.preventDefault();
    setError("");
    if (!isAuthed) { nav("/login"); return; }
    const amt = parseFloat(bidAmount);
    if (isNaN(amt) || amt < minBid) {
      setError(`Bid must be at least ${money(minBid)}`);
      return;
    }
    setPlacing(true);
    try {
      const { data } = await api.post(`/auctions/${id}/bids`, { amount: amt });
      setAuction(data.auction);
      setBidAmount("");
      toast.success(data.extended ? "Bid placed — timer extended!" : "Bid placed!");
      const b = await api.get(`/auctions/${id}/bids?limit=25`);
      setBids(b.data);
    } catch (err) {
      const msg = formatError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setPlacing(false);
    }
  };

  const toggleWatch = async () => {
    if (!isAuthed) { nav("/login"); return; }
    try {
      if (watching) {
        await api.delete(`/watchlist/${id}`);
        setWatching(false);
      } else {
        await api.post(`/watchlist`, { auction_id: id });
        setWatching(true);
        toast.success("Added to watchlist");
      }
    } catch (e) { toast.error(formatError(e)); }
  };

  const checkout = async () => {
    setPayLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        auction_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatError(e));
      setPayLoading(false);
    }
  };

  const boostAuction = async () => {
    setBoostLoading(true);
    try {
      const { data } = await api.post("/boost/checkout", {
        auction_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatError(e));
      setBoostLoading(false);
    }
  };


  const buyerPremium = ((auction.buyer_premium_pct || 0) * currentBid) / 100;
  const total = currentBid + buyerPremium;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
      <Link to="/browse" className="text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-orange-500">← Back to Browse</Link>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8">
        {/* GALLERY + INFO */}
        <div>
          <div className="relative aspect-[4/3] bg-zinc-950 border border-zinc-800 overflow-hidden">
            <img
              data-testid="auction-hero-image"
              src={galleryImages[activeImg]}
              alt={auction.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4 flex items-center gap-2">
              {auction.status === "active" && (
                <span className="inline-flex items-center gap-2 bg-black/70 backdrop-blur border border-white/10 px-3 py-1.5 text-[10px] font-semibold tracking-[0.25em] uppercase text-white">
                  <span className="w-1.5 h-1.5 bg-orange-500 live-dot rounded-full"></span>
                  Live Auction
                </span>
              )}
              {auction.status === "sold" && (
                <span className="bg-emerald-600 text-white px-3 py-1.5 text-[10px] font-semibold tracking-[0.25em] uppercase">
                  Sold
                </span>
              )}
              {auction.status === "ended" && (
                <span className="bg-zinc-700 text-white px-3 py-1.5 text-[10px] font-semibold tracking-[0.25em] uppercase">
                  Ended — No Bids
                </span>
              )}
            </div>
          </div>

          {galleryImages.length > 1 && (
            <div className="mt-3 grid grid-cols-6 gap-2">
              {galleryImages.map((src, i) => (
                <button
                  key={`${src}-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square border ${i === activeImg ? "border-orange-500" : "border-zinc-800 hover:border-zinc-600"} overflow-hidden`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-8">
            <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-2">
              {categoryLabel(auction.category)} · UNIT #{auction.unit_number || "N/A"}
            </div>
            <h1 data-testid="auction-title" className="font-display text-3xl sm:text-5xl font-black uppercase tracking-tighter leading-none">
              {auction.title}
            </h1>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Meta icon={<Building2 className="w-4 h-4" />} label="Facility" value={auction.facility_name} />
              <Meta icon={<MapPin className="w-4 h-4" />} label="Location" value={`${auction.city}, ${auction.state}`} />
              <Meta icon={<Ruler className="w-4 h-4" />} label="Unit Size" value={auction.unit_size} />
              <Meta icon={<Eye className="w-4 h-4" />} label="Views" value={auction.view_count} />
            </div>

            <div className="mt-10 border-t border-zinc-800 pt-8">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">Description</div>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-line">{auction.description}</p>
              {auction.content_hints && auction.content_hints.trim() && (
                <div className="mt-4 bg-orange-500/5 border-l-2 border-orange-500 pl-4 py-2">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-1">Seller&apos;s Note</div>
                  <p className="text-sm text-zinc-300 italic">{auction.content_hints}</p>
                </div>
              )}
            </div>

            <div className="mt-10 border-t border-zinc-800 pt-8">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-4">Auction Terms</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-zinc-300">
                <li className="flex gap-2"><Package className="w-4 h-4 text-orange-500 mt-0.5" /> Buyer&apos;s Premium: <b>{auction.buyer_premium_pct}%</b></li>
                <li className="flex gap-2"><Gavel className="w-4 h-4 text-orange-500 mt-0.5" /> Bid Increment: <b>{money(auction.bid_increment)}</b></li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-orange-500 mt-0.5" /> Auto-extend: <b>{auction.auto_extend_minutes} min</b></li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-orange-500 mt-0.5" /> Pickup within <b>48 hours</b> of win</li>
              </ul>
            </div>
          </div>
        </div>

        {/* BID PANEL */}
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="bg-zinc-950 border border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Current Bid</div>
              {wsConnected && (
                <div data-testid="ws-status" className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-emerald-500">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot"></span> Live
                </div>
              )}
            </div>
            <div data-testid="current-bid-amount" className="font-display text-5xl font-black text-orange-500 tabular-nums leading-none">
              {money(currentBid)}
            </div>
            <div className="flex items-center gap-2 text-xs text-zinc-500 mt-2">
              <Gavel className="w-3 h-3" /> <span data-testid="bid-count">{auction.bid_count} bid{auction.bid_count === 1 ? "" : "s"}</span> · Started at {money(auction.starting_bid)}
            </div>

            <div className="mt-6 pt-6 border-t border-zinc-800">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Ends In</div>
              <Countdown endTime={auction.end_time} className="text-3xl" />
            </div>

            {auction.status === "active" && !isOwner && (
              <form onSubmit={placeBid} className="mt-6 pt-6 border-t border-zinc-800 space-y-3">
                <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 block">
                  Your Bid (min {money(minBid)})
                </label>
                <div className="flex gap-0">
                  <span className="bg-zinc-900 border border-zinc-800 border-r-0 px-3 flex items-center text-zinc-500 font-display text-lg">$</span>
                  <Input
                    data-testid="bid-amount-input"
                    type="number"
                    step="0.01"
                    min={minBid}
                    value={bidAmount}
                    onChange={(e) => setBidAmount(e.target.value)}
                    placeholder={String(minBid)}
                    className="rounded-none bg-black border-zinc-800 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-0 industrial-input h-12 text-lg tabular-nums"
                  />
                </div>
                {error && <div data-testid="bid-error" className="text-red-500 text-xs">{error}</div>}
                <Button
                  type="submit"
                  data-testid="place-bid-button"
                  disabled={placing}
                  className="w-full rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-12 text-sm uppercase tracking-widest font-semibold"
                >
                  {placing ? "Placing..." : (isAuthed && !rulesAccepted) ? "Review Rules & Bid" : `Place Bid ${bidAmount ? money(bidAmount) : ""}`}
                </Button>
                {isAuthed && (
                  <div className="flex items-center justify-between text-xs">
                    {rulesAccepted ? (
                      <span data-testid="rules-status-accepted" className="inline-flex items-center gap-1.5 text-emerald-500">
                        <CheckCircle2 className="w-3 h-3" /> Rules accepted
                      </span>
                    ) : (
                      <span data-testid="rules-status-pending" className="inline-flex items-center gap-1.5 text-orange-500">
                        <ScrollText className="w-3 h-3" /> Rules required to bid
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowRulesModal(true)}
                      data-testid="view-rules-button"
                      className="text-zinc-500 hover:text-orange-500 underline underline-offset-2"
                    >
                      View rules
                    </button>
                  </div>
                )}
                {!isAuthed && (
                  <div className="text-xs text-zinc-500 text-center">
                    <Link to="/login" className="text-orange-500 hover:underline">Log in</Link> to place a bid
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={toggleWatch}
                  data-testid="watch-button"
                  className="w-full rounded-none bg-transparent border-zinc-700 hover:border-orange-500 hover:bg-zinc-900 text-xs uppercase tracking-widest"
                >
                  <Heart className={`w-4 h-4 mr-2 ${watching ? "fill-orange-500 text-orange-500" : ""}`} />
                  {watching ? "Watching" : "Add to Watchlist"}
                </Button>
              </form>
            )}

            {isOwner && (
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <div className="text-sm text-zinc-400 mb-4">You are the seller of this auction.</div>
                {!auction.is_featured && auction.status === "active" && (
                  <div className="bg-black/40 border border-orange-500/40 p-4">
                    <div className="flex items-center gap-2 text-orange-500 mb-2">
                      <Sparkles className="w-4 h-4" />
                      <span className="text-[10px] tracking-[0.25em] uppercase font-semibold">Boost this listing</span>
                    </div>
                    <p className="text-xs text-zinc-400 leading-relaxed">
                      Pin your auction to the top of Browse & Home for 7 days. Sellers who boost see 2–3x more bids on average.
                    </p>
                    <Button
                      onClick={boostAuction}
                      disabled={boostLoading}
                      data-testid="boost-button"
                      className="mt-3 w-full rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-11 text-xs uppercase tracking-widest font-semibold"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      {boostLoading ? "Redirecting…" : "Feature for $15 / 7 days"}
                    </Button>
                  </div>
                )}
                {auction.is_featured && (
                  <div className="bg-orange-500/10 border border-orange-500 p-4 text-orange-400 text-xs uppercase tracking-widest font-semibold flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> Featured until {auction.featured_until ? new Date(auction.featured_until).toLocaleDateString() : "—"}
                  </div>
                )}
              </div>
            )}

            {isWinner && !isPaid && (
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-500 mb-2">You won this auction</div>
                <div className="text-sm text-zinc-400 mb-4">
                  Winning bid: <b className="text-white">{money(currentBid)}</b><br />
                  Buyer&apos;s premium ({auction.buyer_premium_pct}%): <b className="text-white">{money(buyerPremium)}</b><br />
                  Total due: <b className="text-orange-500 font-display text-lg">{money(total)}</b>
                </div>
                <Button
                  data-testid="checkout-button"
                  onClick={checkout}
                  disabled={payLoading}
                  className="w-full rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-12 text-sm uppercase tracking-widest font-semibold"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {payLoading ? "Redirecting…" : "Pay Now"}
                </Button>
              </div>
            )}

            {isWinner && isPaid && (
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <div className="inline-flex items-center gap-2 text-emerald-500 text-xs uppercase tracking-widest font-semibold">
                  <CheckCircle2 className="w-4 h-4" /> Paid — Pickup at facility
                </div>
              </div>
            )}
          </div>

          {/* Bid History */}
          <div className="mt-4 bg-zinc-950 border border-zinc-800 p-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-4">Bid History</div>
            {bids.length === 0 ? (
              <div className="text-sm text-zinc-500">No bids yet — be the first.</div>
            ) : (
              <ul data-testid="bid-history" className="space-y-2 max-h-72 overflow-auto pr-2">
                {bids.map((b, i) => (
                  <li key={b.id} className={`flex items-center justify-between text-sm py-2 border-b border-zinc-900 last:border-0 ${i === 0 ? "text-orange-500 font-semibold" : "text-zinc-400"}`}>
                    <span className="uppercase tracking-widest text-xs">{b.bidder_name}</span>
                    <span className="font-display font-bold tabular-nums">{money(b.amount)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>

      <RulesModal
        open={showRulesModal}
        onOpenChange={setShowRulesModal}
        auctionId={id}
        onAccepted={() => setRulesAccepted(true)}
      />
    </div>
  );
}

function Meta({ icon, label, value }) {
  return (
    <div className="border border-zinc-800 p-4">
      <div className="flex items-center gap-2 text-zinc-500 mb-1">
        {icon}
        <span className="text-[10px] tracking-[0.2em] uppercase">{label}</span>
      </div>
      <div className="text-sm text-white font-semibold truncate">{value}</div>
    </div>
  );
}
